import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, Sparkles, AlertCircle, Users, MapPin, Briefcase, Globe } from 'lucide-react'
import { ProfilePictureUploadGeneric } from '@/components/profile/profile-picture-upload-generic'
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog'
import { useAIWritingAssistant, type ToneOption, type SizeOption } from '@/lib/hooks/use-ai-writing-assistant'
import { cn } from '@/lib/utils'

interface ClientBasicInfoFormProps {
  user: ReturnType<typeof useUser>['user'] | null | undefined
  companyName: string
  clientBio: string
  clientEmail: string
  profilePictureUrl: string | null
  companySize?: string
  industry?: string
  location?: string
  websiteUrl?: string
  onCompanyNameChange: (value: string) => void
  onClientBioChange: (value: string) => void
  onCompanySizeChange?: (value: string) => void
  onIndustryChange?: (value: string) => void
  onLocationChange?: (value: string) => void
  onWebsiteUrlChange?: (value: string) => void
  onImageUploaded: (imageUrl: string) => void
  onImageRemoved: () => void
}

export function ClientBasicInfoForm({
  user,
  companyName,
  clientBio,
  clientEmail,
  profilePictureUrl,
  companySize,
  industry,
  location,
  websiteUrl,
  onCompanyNameChange,
  onClientBioChange,
  onCompanySizeChange,
  onIndustryChange,
  onLocationChange,
  onWebsiteUrlChange,
  onImageUploaded,
  onImageRemoved,
}: ClientBasicInfoFormProps) {
  const { generateClientBio, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [detailsErrors, setDetailsErrors] = useState<{
    companySize?: string;
    location?: string;
    industry?: string;
    websiteUrl?: string;
  }>({});

  // URL validation function
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (optional field)
    
    const trimmedUrl = url.trim();
    
    // Try to create a URL object to ensure it's actually valid
    try {
      let normalizedUrl = trimmedUrl;
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      const urlObj = new URL(normalizedUrl);
      const hostname = urlObj.hostname;
      
      if (!hostname || hostname.length === 0) {
        return false;
      }
      
      // Special case for localhost
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return true;
      }
      
      // Must contain at least one dot
      if (!hostname.includes('.')) {
        return false;
      }
      
      // Cannot start or end with a dot
      if (hostname.startsWith('.') || hostname.endsWith('.')) {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  };

  const handleGenerateBio = async (
    currentText: string | undefined, 
    options: {
      customInstructions: string;
      tone?: ToneOption;
      size?: SizeOption;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ) => {
    return await generateClientBio(currentText ?? clientBio, {
      customInstructions: options.customInstructions,
      tone: options.tone,
      size: options.size,
      iterationPrompt: options.iterationPrompt,
      previousGeneratedText: options.previousGeneratedText,
    });
  };

  const handleAcceptBio = (text: string) => {
    onClientBioChange(text);
    resetAIData();
  };

  return (
    <>
      <div className="flex justify-center pb-2">
        <ProfilePictureUploadGeneric
          profileType="client"
          profileId={null}
          currentUrl={profilePictureUrl || null}
          name={companyName || 'Company'}
          onImageUploaded={onImageUploaded}
          onImageRemoved={onImageRemoved}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          First Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={user?.firstName || ''}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Last Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={user?.lastName || ''}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <Input
          type="email"
          value={clientEmail}
          readOnly
          disabled
          className="h-12 rounded-xl border-2 border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Company Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={companyName}
          onChange={(e) => onCompanyNameChange(e.target.value)}
          placeholder="Acme Corporation"
          className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
          required
          autoFocus
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-slate-700">
            Company Bio <span className="text-red-500">*</span>
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsAIDialogOpen(true)}
            disabled={isAIGenerating}
            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
          >
            {isAIGenerating && generatingType === 'client_bio' ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3 mr-1" />
                Write company bio with AI
              </>
            )}
          </Button>
        </div>
        <AIAssistantDialog
          open={isAIDialogOpen}
          onOpenChange={(open) => {
            setIsAIDialogOpen(open);
            if (!open) {
              resetAIData();
            }
          }}
          onGenerate={handleGenerateBio}
          onAccept={handleAcceptBio}
          generatedText={aiGeneratedData?.type === 'client_bio' ? aiGeneratedData.text : null}
          isLoading={isAIGenerating && generatingType === 'client_bio'}
          type="client_bio"
          currentText={clientBio}
        />
        <Textarea
          value={clientBio}
          onChange={(e) => onClientBioChange(e.target.value)}
          placeholder="Tell us about your company..."
          className="min-h-[100px] rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 resize-none"
          required
          maxLength={1500}
        />
        <p className="text-xs mt-1">
          {clientBio.length < 100 ? (
            <span className="text-amber-500">Characters should be between 100 and 1500 ({clientBio.length}/1500)</span>
          ) : (
            <span className="text-slate-500">{clientBio.length}/1500 characters</span>
          )}
        </p>
      </div>

      {/* Optional Company Details Section */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Company Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Company Size
            </label>
            <Select
              value={companySize || undefined}
              onValueChange={(value) => {
                onCompanySizeChange?.(value);
                if (detailsErrors.companySize) {
                  setDetailsErrors(prev => ({ ...prev, companySize: undefined }));
                }
              }}
            >
              <SelectTrigger className={cn(
                "!h-12 w-full rounded-xl border-2 focus:ring-0 bg-white",
                detailsErrors.companySize
                  ? "border-red-500 focus:border-red-500"
                  : "border-slate-200 focus:border-[#4669ed]"
              )}>
                <SelectValue placeholder="Select company size" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-2 border-slate-200">
                <SelectItem value="startup" className="focus:bg-slate-200 focus:text-slate-900">Startup (1-10 employees)</SelectItem>
                <SelectItem value="small" className="focus:bg-slate-200 focus:text-slate-900">Small (11-50 employees)</SelectItem>
                <SelectItem value="medium" className="focus:bg-slate-200 focus:text-slate-900">Medium (51-200 employees)</SelectItem>
                <SelectItem value="enterprise" className="focus:bg-slate-200 focus:text-slate-900">Enterprise (200+ employees)</SelectItem>
              </SelectContent>
            </Select>
            {detailsErrors.companySize && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {detailsErrors.companySize}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <MapPin className="inline h-4 w-4 mr-1" />
              Location
            </label>
            <Input
              value={location || ''}
              onChange={(e) => {
                onLocationChange?.(e.target.value);
                if (detailsErrors.location) {
                  setDetailsErrors(prev => ({ ...prev, location: undefined }));
                }
              }}
              placeholder="San Francisco, CA"
              className={cn(
                "h-12 rounded-xl border-2 focus:ring-0",
                detailsErrors.location
                  ? "border-red-500 focus:border-red-500"
                  : "border-slate-200 focus:border-[#4669ed]"
              )}
            />
            {detailsErrors.location && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {detailsErrors.location}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Briefcase className="inline h-4 w-4 mr-1" />
              Industry
            </label>
            <Input
              value={industry || ''}
              onChange={(e) => {
                onIndustryChange?.(e.target.value);
                if (detailsErrors.industry) {
                  setDetailsErrors(prev => ({ ...prev, industry: undefined }));
                }
              }}
              placeholder="e.g., Healthcare, Finance, E-commerce"
              className={cn(
                "h-12 rounded-xl border-2 focus:ring-0",
                detailsErrors.industry
                  ? "border-red-500 focus:border-red-500"
                  : "border-slate-200 focus:border-[#4669ed]"
              )}
            />
            {detailsErrors.industry && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {detailsErrors.industry}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1" />
              Company Website
            </label>
            <Input
              type="text"
              inputMode="url"
              value={websiteUrl || ''}
              onChange={(e) => {
                onWebsiteUrlChange?.(e.target.value);
                if (detailsErrors.websiteUrl) {
                  setDetailsErrors(prev => ({ ...prev, websiteUrl: undefined }));
                }
              }}
              onBlur={() => {
                // Validate URL on blur if there's a value
                if (websiteUrl?.trim() && !isValidUrl(websiteUrl.trim())) {
                  setDetailsErrors(prev => ({
                    ...prev,
                    websiteUrl: 'Please enter a valid URL'
                  }));
                } else {
                  setDetailsErrors(prev => ({ ...prev, websiteUrl: undefined }));
                }
              }}
              placeholder="www.example.com"
              className={cn(
                "h-12 rounded-xl border-2 focus:ring-0",
                detailsErrors.websiteUrl
                  ? "border-red-500 focus:border-red-500"
                  : "border-slate-200 focus:border-[#4669ed]"
              )}
            />
            {detailsErrors.websiteUrl && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {detailsErrors.websiteUrl}
              </p>
            )}
          </div>
        </div>
      </div>
      
    </>
  )
}
